import React, {ReactNode} from "react";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {AdminGlobals} from "../Admin";
import Exception from "../../shared/exception/Exception";
import MessageBox from "../../shared/components/MessageBox";
import {MessageType} from "../../shared/log/MessageInterface";
import ErrorCode from "../../shared/exception/ErrorCode";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {HelperGlobals} from "../../shared/components/Helper";
import {Button} from "primereact/button";
import {isMobile} from "react-device-detect";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export interface AbstractPanelProperties extends AdminGlobals, I18nGlobals, HelperGlobals {
}

export interface AbstractPanelState {
	exception?: Exception;
	submitted: boolean;
	hasChanges: boolean;
}

export abstract class AbstractPanel<PROPERTY_TYPE extends AbstractPanelProperties = any,
	STATUS_TYPE extends AbstractPanelState = any> extends PortalPureComponent<PROPERTY_TYPE, STATUS_TYPE> {

	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			submitted: false,
			hasChanges: false
		} as STATUS_TYPE;
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		window.addEventListener('keydown', this.handleKeydown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		window.removeEventListener('keydown', this.handleKeydown);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		this.setState({
			exception: undefined,
			submitted: true
		});
	}

	/**
	 * Handles a exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.WEBSERVICE_SESSION_TIMEOUT:
				try {
					await this.props.context.session.refresh();
					return true
				} catch (exception: any) {
					await this.onError(exception);
				}
				break;
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_FOUND:
			case ErrorCode.WEBSERVICE_SESSION_EXPIRED:
			case ErrorCode.WEBSERVICE_SESSION_UNABLE_TO_CREATE:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_ALLOWED:
			case ErrorCode.WEBSERVICE_SESSION_UNKNOWN_ERROR:
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_NOT_PROVIDED:
				window.location.reload();
				break;
			default:
				this.setState({
					submitted: false,
					exception: portalException
				});
				break;
		}

		return false;
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		// no default behaviour
	}

	protected renderHelpButton(): ReactNode {
		return (
			<Button icon="icon icon-help" className={this.props.showHelp ? " active" : ""} style={{marginLeft: "auto"}}
					onClick={() => this.props.context.getPage().setState({
						showHelp: !this.props.showHelp,
					})}/>
		);
	}

	protected renderError(error?: Exception): ReactNode {
		let exception: Exception | undefined = error || this.state.exception;

		if (!FunctionHelper.exists(exception)) {
			return null;
		}

		let codes: string = "";
		if (FunctionHelper.exists(exception!.code)) {
			codes += "(errorcode: " + exception!.code;

			if (FunctionHelper.exists(exception!.exitCode)) {
				codes += ", exitcode: " + exception!.exitCode;
			}

			codes += ")";
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
							onClose={() => this.setState({
								exception: undefined
							})}
							stacktrace={
								<>
									<p>{exception!.message} {codes}</p>
									<pre>{exception!.stack}</pre>
								</>
							}>
					{exception!.message}
				</MessageBox>
			</div>
		);
	}

	protected renderFieldError(value: string): ReactNode {
		if (!isMobile || FunctionHelper.isEmpty(value)) {
			return null;
		}

		return (
			<span className="field-error">{value}</span>
		);
	}
}